import React, { useState } from "react";
import logo from "../assets/images/logo.png";
import cancel from "../assets/images/navbar/cancel.png";
import arrow from "../assets/images/order/orange-arrow.png";
import { authService } from "../services";

type SigninModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onGetStarted?: () => void;
  mobileNumber: string;
  setMobileNumber: (number: string) => void;
};

const SigninModal: React.FC<SigninModalProps> = ({ isOpen, onClose, onGetStarted, mobileNumber, setMobileNumber }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  
  if (!isOpen) return null;

  const handleGetStarted = async () => {
    if (mobileNumber.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    
    setIsLoading(true);
    setError("");
    
    try {
      const phoneNumber = `+91${mobileNumber}`;
      const response: any = await authService.sendOtp(phoneNumber);
      
      if (response.status) {
        if (onGetStarted) {
          onGetStarted();
        }
      }
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      setError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1003] transition-colors duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1004] pointer-events-none">
        <div
          className="w-[545px] p-8 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div
            className="absolute top-6 right-6 h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
          >
            <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
          </div>

          {/* Logo */}
          <div className="mb-[36px]">
            <img src={logo} alt="Duddus" className="h-[54px] w-[166px]" />
          </div>

          {/* Title */}
          <h2 className="text-[#050505] dark:text-white font-urbanist text-[32px] font-semibold leading-[125%] mb-[8px]">
            Sign In to continue...
          </h2>

          {/* Subtitle */}
          <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-medium leading-[140%] mb-[24px]">
            Enter your mobile number to get started
          </p>

          {/* Mobile Number Input */}
          <div className="flex items-center gap-2 border border-[#E0E0E0] dark:border-gray-600 rounded-[12px] px-4 py-3 mb-[24px] dark:bg-gray-700 transition-colors">
            <span className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium">
              +91
            </span>
            <div className="h-[24px] w-[1px] bg-[#E0E0E0] dark:bg-gray-600"></div>
            <input
              type="text"
              placeholder="Enter your mobile number"
              value={mobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setMobileNumber(value);
              }}
              className="flex-1 outline-none text-[#050505] dark:text-white dark:bg-gray-700 font-urbanist text-[16px] placeholder:text-[#BDBDBD] dark:placeholder:text-gray-500"
              maxLength={10}
            />
          </div>
          
          {/* Error Message */}
          {error && (
            <p className="text-red-500 font-urbanist text-[14px] mb-[16px]">
              {error}
            </p>
          )}

          {/* Get Started Button */}
          <button
            onClick={handleGetStarted}
            disabled={isLoading}
            className="w-full flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-[76px]"
          >
            <span className="text-white font-urbanist text-[20px] font-semibold leading-[140%]">
              {isLoading ? "Sending OTP..." : "Get Started"}
            </span>
            <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
              <img
                src={arrow}
                alt="right arrow"
                className="h-[41px] w-[30px] stroke-[2.587px] stroke-[#FB8606]"
              />
            </div>
          </button>
        </div>
      </div>
    </>
  );
};

export default SigninModal;
